import React, { useState } from "react";
import { Card } from "primereact/card";
import { Divider } from "primereact/divider";
import { PageHeader } from "../components/PageHeader";
import imgWhy1 from "../assets/carlos-muza-hpjSkU2UYSU-unsplash.jpg";
import imgWhy2 from "../assets/stephen-dawson-qwtCeJ5cLYs-unsplash.jpg";
import imgWhy3 from "../assets/balazs-ketyi-FeuEg-8XlA8-unsplash.jpg";
import imgWhy4 from "../assets/new-data-services-Ar-iTL4QKl4-unsplash.jpg";
import heroImage from "../assets/artem-sapegin-b18TRXc8UPQ-unsplash.jpg";
import { Button } from "primereact/button";
import { Link, useNavigate } from "react-router-dom";
import { PortfolioLightbox } from "../components/PortfolioLightbox";

// Use real, unused assets from /src/assets here (kept heroImage as a safe placeholder)
const whyImages = [imgWhy1, imgWhy2, imgWhy3, imgWhy4];

const services = [
  {
    id: 1,
    icon: "pi pi-code",
    title: "Web Development",
    description: "Full-stack web applications",
    features: ["React", "Node.js", "TypeScript"],
    price: "Custom",
    bgColor: "bg-blue-100 dark:bg-blue-500",
    textColor: "text-blue-600 dark:text-white",
  },
  {
    id: 2,
    icon: "pi pi-desktop",
    title: "Custom Applications",
    description: "Cloud-native or local storage",
    features: ["React", "Node.js", "TypeScript"],
    price: "Custom",
  },
  {
    id: 3,
    icon: "pi pi-database",
    title: "Database Design",
    description: "Scalable data architecture",
    features: ["MS Sql", "PostgreSQL", "MongoDB"],
    price: "Custom",
  },
  {
    id: 4,
    icon: "pi pi-cloud",
    title: "Cloud Solutions",
    description: "Azure and AWS deployment",
    features: ["MS Azure", "AWS", "Docker", "CI/CD"],
    price: "Custom",
    bgColor: "bg-purple-100 dark:bg-purple-500",
    textColor: "text-purple-600 dark:text-white",
  },
  {
    id: 5,
    icon: "pi pi-shield",
    title: "Security Audit",
    description: "Vulnerability assessment",
    features: [
      "Penetration Testing",
      "OWASP",
      "Physical Security",
      "Network Security",
    ],
    price: "Custom",
  },
  {
    id: 6,
    icon: "pi pi-chart-line",
    title: "Analytics",
    description: "Data visualization dashboards",
    features: ["D3.js", "Power BI", "Chart.js", "Tableau"],
    price: "Custom",
  },
];

export const ServicesPage: React.FC = () => {
  const [showPortfolio, setShowPortfolio] = useState(false);
  const navigate = useNavigate();

  return (
    <>
      {/* Hero Section */}
      <PageHeader
        title="Our Services"
        description="Comprehensive technology solutions tailored to drive your business forward"
        backgroundImage={heroImage}
        height="500px"
        buttons={[
          {
            label: "Get Started",
            icon: "pi pi-arrow-right",
            onClick: () => window.location.assign("/contact#contact-form"),
          },
          {
            label: "Schedule Consultation",
            icon: "pi pi-calendar",
            severity: "secondary",
            outlined: true,
            onClick: () => {
              navigate("/contact");
              setTimeout(() => {
                document
                  .getElementById("contact-form")
                  ?.scrollIntoView({ behavior: "smooth", block: "start" });
              }, 0);
            },
          },
        ]}
      />

      {/* Services Overview Section */}
      <br />
      <br />
      <div
        id="services-overview"
        className="container mx-auto px-8 py-32 md:py-40 mb-8 md:mb-16"
      >
        <div className="text-center mb-12">
          <h2 className="text-5xl md:text-6xl font-bold mb-8 tracking-tight text-gray-900 dark:text-white">
            What We Offer
          </h2>
          <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-loose">
            From concept to deployment, we provide end-to-end technology
            solutions that transform your business challenges into opportunities
            for growth and innovation.
          </p>
        </div>
      </div>

      {/* Core Services Grid Section */}
      <section className="w-full pt-24 pb-48 md:pb-64 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-5xl md:text-6xl font-bold text-center mb-20 tracking-tight text-gray-900 dark:text-white">
            Core Services
          </h2>

          <br />
          <br />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <Card key={service.id} className="h-full">
                <div className="p-6">
                  <i
                    className={`${service.icon} text-4xl text-indigo-600 mb-4`}
                  ></i>
                  <h3 className="text-xl font-bold mb-2">{service.title}</h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <ul className="space-y-2 mb-4">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-sm">
                        <i className="pi pi-check text-green-500 mr-2"></i>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Button label="Learn More" className="w-full" />
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <br />
      <br />
      <br />

      {/* Why Choose Us Section */}
      <div
        id="why-choose-us"
        className="container mx-auto px-8 pt-24 md:pt-32 pb-32 md:pb-40 mb-8 md:mb-16"
      >
        <div className="text-center mb-16">
          <h2 className="text-5xl md:text-6xl font-bold mb-8 tracking-tight text-gray-900 dark:text-white">
            Why Choose Logic Innova
          </h2>
          <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-loose">
            We combine technical expertise with business acumen to deliver
            solutions that truly make a difference.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 max-w-5xl mx-auto">
          {/* Text Block 1 */}
          <div className="p-8 bg-blue-50 dark:bg-gray-800 rounded-xl shadow-lg border-l-4 border-blue-500 text-left">
            <div className="space-y-4">
              {/* icon row → image row */}
              <div className="grid grid-cols-[48px_1fr] items-center gap-4">
                <div className="w-12 h-24 rounded-md overflow-hidden ring-2 ring-blue-500/70 dark:ring-blue-400/70 shadow-md flex-shrink-0">
                  <img
                    src={whyImages[0]}
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-cover blur-[1.5px] scale-110"
                  />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white m-0 text-left break-words">
                  Data-informed decisions
                </h3>
              </div>
              <p className="text-base text-gray-700 dark:text-gray-300 leading-relaxed">
                We surface the metrics that matter. From product analytics to
                operational KPIs, every build is guided by measurable outcomes.
              </p>
            </div>
          </div>

          {/* Text Block 2 */}
          <div className="p-8 bg-purple-50 dark:bg-gray-800 rounded-xl shadow-lg border-r-4 border-purple-500 text-left">
            <div className="space-y-4">
              <div className="grid grid-cols-[48px_1fr] items-center gap-4">
                <div className="w-12 h-24 rounded-md overflow-hidden ring-2 ring-purple-500/70 dark:ring-purple-400/70 shadow-md flex-shrink-0">
                  <img
                    src={whyImages[1]}
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-cover blur-[1.5px] scale-110"
                  />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white m-0 text-left break-words">
                  Senior engineering, start to finish
                </h3>
              </div>
              <p className="text-base text-gray-700 dark:text-gray-300 leading-relaxed">
                Small, senior teams do the work: architecture, implementation,
                and CI/CD. Weekly touchpoints, transparent tracking, no
                hand‑offs.
              </p>
            </div>
          </div>

          {/* Text Block 3 */}
          <div className="p-8 bg-green-50 dark:bg-gray-800 rounded-xl shadow-lg border-l-4 border-green-500 text-left">
            <div className="space-y-4">
              <div className="grid grid-cols-[48px_1fr] items-center gap-4">
                <div className="w-12 h-24 rounded-md overflow-hidden ring-2 ring-green-500/70 dark:ring-green-400/70 shadow-md flex-shrink-0">
                  <img
                    src={whyImages[2]}
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-cover blur-[1.5px] scale-110"
                  />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white m-0 text-left break-words">
                  User-centered product craft
                </h3>
              </div>
              <p className="text-base text-gray-700 dark:text-gray-300 leading-relaxed">
                From mobile to web, we design flows that reduce friction and
                increase adoption—validated with rapid iterations and feedback.
              </p>
            </div>
          </div>

          {/* Text Block 4 */}
          <div className="p-8 bg-orange-50 dark:bg-gray-800 rounded-xl shadow-lg border-r-4 border-orange-500 text-left">
            <div className="space-y-4">
              <div className="grid grid-cols-[48px_1fr] items-center gap-4">
                <div className="w-12 h-24 rounded-md overflow-hidden ring-2 ring-orange-500/70 dark:ring-orange-400/70 shadow-md flex-shrink-0">
                  <img
                    src={whyImages[3]}
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-cover blur-[1.5px] scale-110"
                  />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white m-0 text-left break-words">
                  Cloud-ready, secure by design
                </h3>
              </div>
              <p className="text-base text-gray-700 dark:text-gray-300 leading-relaxed">
                Azure/AWS best practices, IaC, automated testing, and
                observability baked in—so you can scale confidently and stay
                compliant.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Divider className="my-48 md:my-64 opacity-0" />

      {/* CTA Section */}
      <div className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 dark:from-blue-700 dark:via-blue-800 dark:to-indigo-900 py-32 md:py-40 overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 left-0 w-full h-full opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 rounded-full bg-white blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-40 h-40 rounded-full bg-white blur-3xl"></div>
          <div className="absolute top-1/2 left-1/3 w-24 h-24 rounded-full bg-white blur-2xl"></div>
        </div>

        <div className="container mx-auto px-8 md:px-12 text-center relative z-10">
          {/* Icon Feature Row */}
          <div className="flex justify-center gap-8 md:gap-12 mb-12 md:mb-16 flex-wrap">
            <div className="flex items-center gap-2 text-white/90">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
                <i className="pi pi-building text-white text-lg"></i>
              </div>
              <span className="text-sm font-medium">
                Established Excellence
              </span>
            </div>
            <div className="flex items-center gap-2 text-white/90">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
                <i className="pi pi-globe text-white text-lg"></i>
              </div>
              <span className="text-sm font-medium">Global Reach</span>
            </div>
            <div className="flex items-center gap-2 text-white/90">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
                <i className="pi pi-heart-fill text-white text-lg"></i>
              </div>
              <span className="text-sm font-medium">Client Satisfaction</span>
            </div>
          </div>

          <br />
          <br />

          <h2 className="text-5xl md:text-6xl font-bold text-white mb-8 tracking-tight">
            Ready to Work Together?
          </h2>
          <p className="text-xl md:text-2xl text-white mb-12 md:mb-16 max-w-3xl mx-auto leading-loose">
            Let's start a conversation about how Logic Innova can help your
            organization thrive in the digital age.
          </p>
          <div className="flex gap-5 justify-center flex-wrap">
            <Link to="/contact">
              <Button
                label="Get in Touch"
                icon="pi pi-envelope"
                iconPos="right"
                size="large"
                className="shadow-2xl hover:shadow-3xl transition-all hover:scale-105"
                style={{
                  gap: "0.5rem",
                  padding: "1rem 2rem",
                  fontSize: "1.1rem",
                }}
              />
            </Link>
            <Button
              label="View Our Work"
              icon="pi pi-images"
              onClick={() => setShowPortfolio(true)}
              size="large"
              className="shadow-xl hover:shadow-2xl transition-all hover:scale-105 hover:bg-white/10"
              style={{
                color: "white",
                borderColor: "white",
                borderWidth: "2px",
                gap: "0.5rem",
                padding: "1rem 2rem",
                fontSize: "1.1rem",
              }}
            />
          </div>
        </div>
      </div>

      <PortfolioLightbox
        visible={showPortfolio}
        onHide={() => setShowPortfolio(false)}
      />
    </>
  );
};
